/**
 * Access-logging + object-lock retention hooks for a single bucket. Split out
 * of `buckets.ts` to keep each data-layer file focused and small. All keys carry
 * `serviceId`+`bucket` so caches stay isolated per bucket.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  serviceApiV3,
  unwrap,
  storageBase,
  s3Keys,
  type BucketLogging,
  type ObjectLockRetention,
  type ObjectLockMode,
} from './_shared';

/** Read a bucket's server access-logging config. */
export function useBucketLogging(
  serviceId: string | undefined,
  bucket: string | undefined,
) {
  return useQuery<BucketLogging>({
    queryKey: s3Keys.logging(serviceId ?? '', bucket ?? ''),
    enabled: !!serviceId && !!bucket,
    queryFn: async () =>
      unwrap<BucketLogging>(
        await serviceApiV3.get(`${storageBase(serviceId!)}/buckets/${bucket}/logging`),
      ),
  });
}

export interface SetLoggingInput {
  enabled: boolean;
  target_bucket?: string;
  target_prefix?: string;
}

/** Enable/disable access logging (targets are only sent when enabling). */
export function useSetBucketLogging(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<BucketLogging, Error, SetLoggingInput>({
    mutationFn: async (input) =>
      unwrap<BucketLogging>(
        await serviceApiV3.put(
          `${storageBase(serviceId)}/buckets/${bucket}/logging`,
          input,
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.logging(serviceId, bucket) });
      qc.invalidateQueries({ queryKey: s3Keys.bucket(serviceId, bucket) });
    },
  });
}

/** Read a bucket's default object-lock retention. */
export function useObjectLock(
  serviceId: string | undefined,
  bucket: string | undefined,
) {
  return useQuery<ObjectLockRetention>({
    queryKey: s3Keys.objectLock(serviceId ?? '', bucket ?? ''),
    enabled: !!serviceId && !!bucket,
    queryFn: async () =>
      unwrap<ObjectLockRetention>(
        await serviceApiV3.get(`${storageBase(serviceId!)}/buckets/${bucket}/object-lock`),
      ),
  });
}

export interface SetObjectLockInput {
  mode: ObjectLockMode;
  days?: number;
  years?: number;
}

/** Set the default retention. Backend requires exactly one of days/years > 0. */
export function useSetObjectLock(serviceId: string, bucket: string) {
  const qc = useQueryClient();
  return useMutation<ObjectLockRetention, Error, SetObjectLockInput>({
    mutationFn: async (input) =>
      unwrap<ObjectLockRetention>(
        await serviceApiV3.put(
          `${storageBase(serviceId)}/buckets/${bucket}/object-lock`,
          input,
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: s3Keys.objectLock(serviceId, bucket) });
      qc.invalidateQueries({ queryKey: s3Keys.bucket(serviceId, bucket) });
    },
  });
}
